import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { RichTextEditorInputWithMention, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useCreateJobComment } from "@/hooks/mutations/useCreateJobComment";
import { useAddJobComment } from "@/hooks/sets/useAddJobComment";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const createJobCommentSchema = z.object({
  content: z
    .object({
      type: z.literal("doc"),
      content: z.array(z.any()),
    })
    .refine(
      (data) => {
        return (
          JSON.stringify(data.content) !==
          JSON.stringify(tiptapDefaultJson.content)
        );
      },
      {
        message: "Required",
      },
    ),
});

export const CreateJobCommentForm = () => {
  const { mutate: createJobComment } = useCreateJobComment();
  const addJobComment = useAddJobComment();

  const form = useForm<z.infer<typeof createJobCommentSchema>>({
    defaultValues: {
      content: tiptapDefaultJson,
    },
    resolver: zodResolver(createJobCommentSchema),
  });

  function onSubmit(data: z.infer<typeof createJobCommentSchema>) {
    mixpanelUtils.trackFormSubmission("Create job comment", MIXPANEL_SCREENS.JOB);
    createJobComment(
      {
        createJobCommentRequest: { content: data.content },
      },
      {
        onSuccess: (newJobComment) => {
          form.reset();
          addJobComment({
            jobCommentToAdd: newJobComment,
          });
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RichTextEditorInputWithMention
                  editorContentClasses="pb-20"
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Row className="justify-end">
          <Button className="rounded-full" type="submit" variant="outline">
            Add
          </Button>
        </Row>
      </form>
    </Form>
  );
};
